// The stage as static/css/tesla-videoplayer.css and base.css draw it, in CSS px, for the export to paint the same view.

const SANS = 'Geist, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
const MONO = '"Geist Mono", ui-monospace, "SF Mono", Menlo, monospace';
const BODY_WEIGHT = 200;

export const LINE_HEIGHT = 1.45;
const TABULAR_SUFFIX = ' Tabular';

export interface TextStyle {
  readonly weight: number;
  readonly size: number;
  readonly family: string;
  readonly color: string;
  readonly tabular?: boolean;
  readonly letterSpacing?: number;
}

const FIRST_FAMILY = /^\s*"?([^",]+?)"?\s*(?:,|$)/;

export const tabularFamily = (family: string): string => family + TABULAR_SUFFIX;

export function fontOf(style: TextStyle, tabular = style.tabular ?? false): string {
  const primary = FIRST_FAMILY.exec(style.family)?.[1];
  const family = tabular && primary ? `"${tabularFamily(primary)}", ${style.family}` : style.family;
  return `${style.weight} ${style.size}px ${family}`;
}

export const COLORS = {
  stage: '#070708',
  tile: '#000',
  text: '#F3F3F3',
  muted: 'rgba(243, 243, 243, 0.62)',
  faint: 'rgba(243, 243, 243, 0.42)',
  line: 'rgba(255, 255, 255, 0.08)',
  lineStrong: 'rgba(255, 255, 255, 0.14)',
  control: '#26262A',
  glass: 'rgba(21, 21, 23, 0.82)',
  autopilot: 'oklch(0.76 0.12 240)',
  brake: 'oklch(0.83 0.14 75)',
  brakeTint: 'oklch(0.83 0.14 75 / 0.24)',
  throttle: 'oklch(0.8 0.15 152)',
  blinker: 'oklch(0.92 0.18 106)',
} as const;

export const NO_FOOTAGE = 'No footage from this camera here';
export const NO_TELEMETRY = 'No telemetry here';
export const BLINK_PERIOD_MS = 700;

export const STAGE = {
  inset: 6,
  tileRadius: 10,
  gap: { weight: BODY_WEIGHT, size: 13, family: SANS, color: COLORS.muted },
} as const;

export const CHIP = {
  offset: 8,
  padding: { top: 3, right: 8, bottom: 3, left: 4 },
  gap: 6,
  radius: 7,
  blur: 8,
  text: { weight: 500, size: 12.5, family: SANS, color: COLORS.text },
  key: { size: 18, radius: 5, text: { weight: 500, size: 11, family: MONO, color: COLORS.text } },
  clock: { paddingX: 8, text: { weight: 500, size: 12.5, family: MONO, color: COLORS.text } },
} as const;

export const HUD = {
  bottom: 16,
  padding: 4,
  gap: 2,
  radius: 12,
  blur: 14,
  saturate: 1.4,
  shadow: { y: 10, blur: 30, color: 'rgba(0, 0, 0, 0.45)' },
  ring: COLORS.line,
  cell: { height: 32, padding: 9, gap: 7 },
  speed: {
    gap: 5,
    minWidthCh: 3,
    value: { weight: 500, size: 22, family: MONO, color: COLORS.text, tabular: true, letterSpacing: -0.02 * 22 },
    unit: { weight: BODY_WEIGHT, size: 11.5, family: SANS, color: COLORS.faint },
  },
  text: { weight: 500, size: 13, family: SANS, color: COLORS.text, tabular: true },
  gears: { gap: 1, width: 20, height: 24, radius: 6, text: { weight: 500, size: 13, family: MONO, color: COLORS.faint } },
  brake: { paddingX: 7, paddingY: 2, radius: 6, text: { weight: 600, size: 12, family: SANS, color: COLORS.faint } },
  pedal: { width: 56, height: 5, radius: 999 },
  wheel: { size: 22, viewBox: 24, stroke: 1.6, rims: [8.5, 2.2], spokes: 'M3.6 11h6.2M14.2 11h6.2M12 14.2v6.2' },
  steeringMinWidthCh: 4,
  blinker: { left: '◀', right: '▶', text: { weight: BODY_WEIGHT, size: 12, family: SANS, color: COLORS.faint } },
  g: { size: 26, viewBox: 24, stroke: 1, rings: [11, 5.5], axes: 'M-11 0h22M0 -11v22', dot: 2 },
  empty: { paddingX: 8, paddingY: 4, text: { weight: BODY_WEIGHT, size: 12.5, family: SANS, color: COLORS.muted } },
} as const;
